import React from 'react';
import { Box, Button, Collapse, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { alpha } from '@mui/material/styles';
import { PERIPLO_URL, PERIPLO_BRAND } from '../constants/periplo';
import periploIcon from '../periplo_icon.png';

const DISMISSED_KEY = 'tripgen_periplo_notice_dismissed';
const SHOW_DELAY_MS = 500;

/** Once-per-session notice pointing users to Periplo. */
export const PeriploNotice: React.FC = () => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISSED_KEY)) {
        return;
      }
    } catch {
      // Storage unavailable (private mode / blocked) - still show the notice.
    }
    const timer = window.setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Ignore storage failures; the notice simply reappears next load.
    }
  };

  return (
    <Collapse in={open} timeout={240} mountOnEnter unmountOnExit>
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
        }}
      >
        <Paper
          elevation={0}
          role="region"
          aria-label="Periplo announcement"
          sx={(theme) => ({
            position: 'relative',
            maxWidth: 1320,
            mx: 'auto',
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: {
              xs: 'auto minmax(0, 1fr) auto',
              md: 'auto minmax(0, 1fr) auto auto',
            },
            alignItems: 'center',
            columnGap: { xs: 1.25, sm: 1.5 },
            rowGap: { xs: 1.25, sm: 1 },
            p: { xs: 1.25, sm: 1.5 },
            pr: { xs: 1, sm: 1.25 },
            borderRadius: 1,
            border: `1px solid ${alpha(PERIPLO_BRAND.main, 0.26)}`,
            background: `linear-gradient(90deg, #fffdfb 0%, ${theme.palette.common.white} 54%, #fff3ec 100%)`,
            boxShadow: `
              0 16px 38px ${alpha(theme.palette.common.black, 0.07)},
              0 8px 20px ${alpha(PERIPLO_BRAND.main, 0.1)}
            `,
          })}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: '0 auto 0 0',
              width: 4,
              background: `linear-gradient(180deg, ${PERIPLO_BRAND.light}, ${PERIPLO_BRAND.main})`,
            }}
          />

          <Box
            sx={(theme) => ({
              ml: { xs: 0.25, sm: 0.5 },
              width: { xs: 38, sm: 42 },
              height: { xs: 38, sm: 42 },
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.common.white,
              border: `1px solid ${alpha(PERIPLO_BRAND.main, 0.14)}`,
              boxShadow: `0 10px 22px ${alpha(PERIPLO_BRAND.main, 0.14)}`,
            })}
          >
            <Box
              component="img"
              src={periploIcon}
              alt=""
              sx={{
                width: { xs: 28, sm: 31 },
                height: { xs: 28, sm: 31 },
                borderRadius: '24%',
                display: 'block',
              }}
            />
          </Box>

          <Box sx={{ minWidth: 0, pr: { xs: 0, md: 1 } }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.2,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.05rem' },
              }}
            >
              TripGen has moved to Periplo
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.25,
                lineHeight: 1.35,
                maxWidth: 660,
                fontSize: { xs: '0.86rem', sm: '0.9rem' },
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Continue in the newer planning workspace with faster itineraries and cleaner editing.
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="small"
            component="a"
            href={PERIPLO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={dismiss}
            endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 17 }} />}
            sx={{
              gridColumn: { xs: '1 / -1', md: 'auto' },
              gridRow: { xs: 2, md: 'auto' },
              justifySelf: { xs: 'stretch', md: 'end' },
              minHeight: { xs: 34, sm: 36 },
              textTransform: 'none',
              fontWeight: 750,
              fontSize: { xs: '0.9rem', sm: '0.92rem' },
              px: 1.75,
              backgroundColor: PERIPLO_BRAND.main,
              boxShadow: `0 10px 20px ${alpha(PERIPLO_BRAND.main, 0.18)}`,
              '&:hover': {
                backgroundColor: PERIPLO_BRAND.dark,
                boxShadow: `0 12px 26px ${alpha(PERIPLO_BRAND.dark, 0.2)}`,
              },
            }}
          >
            Open Periplo
          </Button>

          <IconButton
            size="small"
            aria-label="Dismiss Periplo notice"
            onClick={dismiss}
            sx={(theme) => ({
              gridColumn: { xs: 3, md: 'auto' },
              gridRow: { xs: 1, md: 'auto' },
              justifySelf: 'end',
              alignSelf: 'start',
              color: 'text.secondary',
              backgroundColor: theme.palette.common.white,
              border: `1px solid ${alpha(PERIPLO_BRAND.main, 0.14)}`,
              '&:hover': {
                color: PERIPLO_BRAND.dark,
                backgroundColor: '#fff7f2',
              },
            })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Box>
    </Collapse>
  );
};
